import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Zap, AlertCircle } from "lucide-react";
import ReportDashboard from "./ReportDashboard";
import GlobalNav from "@/components/GlobalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SampleReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSample = async () => {
      try {
        const res = await axios.get(`${API}/sample-report`);
        setReport(res.data);
      } catch (err) {
        setError("Failed to load sample report.");
      } finally {
        setLoading(false);
      }
    };
    fetchSample();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6" data-testid="sample-report-loading">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center gap-4 mb-8">
            <Skeleton className="h-10 w-10 bg-[#1A1A1A]" />
            <Skeleton className="h-10 w-64 bg-[#1A1A1A]" />
          </div>
          <div className="grid grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-28 bg-[#1A1A1A]" />)}
          </div>
          <Skeleton className="h-64 bg-[#1A1A1A]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="sample-report-error">
        <Card className="bg-[#121212] border-white/10 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">Error</h2>
            <p className="text-zinc-400 mb-6">{error}</p>
            <Link to="/">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <GlobalNav />
      <div data-testid="sample-report-page">
        <Helmet>
          <title>Sample SEO Audit Report – ScoreCite</title>
          <meta name="description" content="See a sample ScoreCite audit report. Understand what insights you'll get about your website's SEO and AI search readiness." />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href="https://www.scorecite.com/sample-report" />
        </Helmet>
        
        {/* Demo Banner */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-blue-500/20" data-testid="demo-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-sm text-zinc-300">
                  <strong className="text-white">Sample Report</strong> — See what you get in the full version
                </span>
              </div>
              <Link to="/login" data-testid="demo-cta">
                <Button className="bg-blue-600 hover:bg-blue-500 text-white text-sm py-2 px-4">
                  <Zap className="w-4 h-4 mr-2" /> Run your own audit
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Reuse ReportDashboard with static data */}
        <ReportDashboard staticData={report} />
      </div>
    </>
  );
}
